<script lang="ts" setup>
import RCards from '~/components/organs/user/Cards.vue'
import RModalCreateDirectory from '~/components/organs/modals/directories/Create.vue'
import RModalEditDirectory from '~/components/organs/modals/directories/Edit.vue'
import RModalDeleteDirectory from '~/components/organs/modals/directories/Delete.vue'
import RModalPutCardToDirectory from '~/components/organs/modals/directories/PutCard.vue'
import RModalNotAvailableDirectory from '~/components/organs/modals/directories/NotAvailable.vue'
import RModalDeleteFile from '~/components/organs/modals/DeleteFile.vue'
import RModalNameChange from '~/components/organs/modals/NameChange.vue'
import { useCardList } from '~/store/card-list'
import { storeToRefs } from 'pinia'
import { CardType } from '~/static/enums/card-type'
import { useRemovalBackgroundStore } from '~/store/removal-background'
import { useAuth } from '~/store/auth'
import { useDirectoriesStore } from '~/store/directories'
import { closeModal, openModal } from '~/utils/Modal'
import { useEditorStore } from '~/store/editor'
import { BIG_DESKTOP_WIDTH, DESKTOP_WIDTH, TABLET_WIDTH, VERY_BIG_DESKTOP_WIDTH } from '~/static/window-sizing'
import { hasFolderManagement } from '~/static/enums/user/subscription'

interface Props {
  type: CardType
}

const props = defineProps<Props>()

const cardsStore = useCardList()
const { list, isLoading, filters } = storeToRefs(cardsStore)

const cardStore = useEditorStore()
const { nameForChanging, nameError } = storeToRefs(cardStore)

const directoriesStore = useDirectoriesStore()
const { directories } = storeToRefs(directoriesStore)

const authStore = useAuth()
const { isAdmin, user } = storeToRefs(authStore)

onMounted(() => {
  cardsStore.list[props.type].totalCount = undefined
  cardsStore.list[props.type].cards = []
  cardsStore.list[props.type].page = 1
  cardsStore.loadNextStep(props.type)
  directoriesStore.loadData()
})

const load = (): void => {
  cardsStore.loadNextStep(props.type)
}

const imageStore = useRemovalBackgroundStore()

const loadImage = (file: File): void => {
  imageStore.loadImageWithBackgroundRemoved(file)
  useRouter().push('/file')
}

const itemsSise = ref({ height: 0, width: 0 })
const setItemsSize = () => {
  if (window.innerWidth >= VERY_BIG_DESKTOP_WIDTH) {
    itemsSise.value = {
      height: 284,
      width: 196,
    }
  } else if (window.innerWidth >= BIG_DESKTOP_WIDTH) {
    itemsSise.value = {
      height: 244,
      width: 166,
    }
  } else if (window.innerWidth >= DESKTOP_WIDTH) {
    itemsSise.value = {
      height: 310,
      width: 217,
    }
  } else if (window.innerWidth >= TABLET_WIDTH) {
    itemsSise.value = {
      height: ((window.innerWidth - 48) / 4 / 0.755) + 24,
      width: (window.innerWidth - 48) / 4,
    }
  } else {
    itemsSise.value = {
      height: ((window.innerWidth - 48) / 2 / 0.755) + 24,
      width: (window.innerWidth - 48) / 2,
    }
  }
}

onMounted(() => {
  setItemsSize()
  window.addEventListener('resize', setItemsSize)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', setItemsSize)
})

const openCardOrPopup = (id: number) => {
  if (props.type === CardType.ProductCard || isAdmin.value) {
    useRouter().push({
      path: '/file/' + id,
    })
  } else {
    useRouter().push({
      path: '/file',
      query: {
        template_id: id,
      },
    })
  }
}

const directoryNotAvailable = (title: string): boolean => {
  if (!user.value?.subscription || !hasFolderManagement(user.value.subscription.tariff)) {
    openModal(RModalNotAvailableDirectory, { title })
    return true
  }
  return false
}

const cloneCard = async (id: number) => {
  const cardIndex = list.value[props.type].cards.findIndex((card) => {
    return card.id === id
  })
  if (cardIndex < 0) {
    return
  }
  cardsStore.cloneCard(id).then((newCardId) => {
    list.value[props.type].cards.unshift({
      id: newCardId,
      type: CardType.ProductCard,
      name: 'Без названия',
      directory_id: list.value[props.type].cards[cardIndex].directory_id,
      preview: list.value[props.type].cards[cardIndex].preview,
      data: list.value[props.type].cards[cardIndex].data,
    })
  })
}
const editCard = async (id: number) => {
  const cardIndex = list.value[props.type].cards.findIndex((card) => {
    return card.id === id
  })
  if (cardIndex < 0) {
    return
  }
  cardStore.id = id
  nameForChanging.value = list.value[props.type].cards[cardIndex].name
  nameError.value = ''
  const modal = await openModal(RModalNameChange, { name: nameForChanging, error: nameError })
  modal.on('change-name', (value) => {
    nameForChanging.value = value
  })
  modal.on('save', () => {
    cardStore.changeName().then((result) => {
      if (result) {
        list.value[props.type].cards[cardIndex].name = nameForChanging.value
        closeModal()
      }
    })
  })
  modal.on('cancel', () => {
    closeModal()
  })
}
const deleteCard = async (id: number) => {
  const cardIndex = list.value[props.type].cards.findIndex((card) => {
    return card.id === id
  })
  if (cardIndex < 0) {
    return
  }
  const modal = await openModal(RModalDeleteFile, { name: list.value[props.type].cards[cardIndex].name })
  modal.on('delete', () => {
    cardStore.delete(id)
    list.value[props.type].cards.splice(cardIndex, 1)
    closeModal()
  })
}
const putCardToDirectory = async (id: number) => {
  if (
    directoryNotAvailable('Перемещение карточек')
  ) {
    return
  }
  const cardIndex = list.value[props.type].cards.findIndex((card) => {
    return card.id === id
  })
  if (cardIndex < 0) {
    return
  }
  const modal = await openModal(RModalPutCardToDirectory, {
    directories: directories.value,
    directoryId: list.value[props.type].cards[cardIndex].directory_id,
    cardName: list.value[props.type].cards[cardIndex].name,
  })
  modal.on('put', async (value: number | null) => {
    if (value === null) {
      closeModal()
      const modal = await openModal(RModalCreateDirectory)
      modal.on('create', (name: string) => {
        directoriesStore.create(name).then((newDirId) => {
          directoriesStore.putCard(id, newDirId).then(() => {
            closeModal()
            openDirectory(newDirId)
          })
        })
      })
      return
    }
    directoriesStore.putCard(id, value).then(() => {
      closeModal()
      openDirectory(value)
    })
  })
}
const excludeCardFromDirectory = async (id: number) => {
  if (directoryNotAvailable('Перемещение карточек')) {
    return
  }
  directoriesStore.putCard(id, null).then(() => {
    closeModal()
    openDirectory(null)
  })
}

const createDirectory = async () => {
  if (
    directoryNotAvailable('Создание папок')
  ) {
    return
  }
  const modal = await openModal(RModalCreateDirectory)
  modal.on('create', (name: string) => {
    directoriesStore.create(name)
  })
}
const editDirectory = async (id: number) => {
  if (directoryNotAvailable('Управление папками')) {
    return
  }
  const directory = directories.value.find((dir) => {
    return dir.id === id
  })
  if (directory === undefined) {
    return
  }
  const modal = await openModal(RModalEditDirectory, {
    oldName: directory.name,
  })
  modal.on('edit', (name: string) => {
    directoriesStore.changeName(id, name)
  })
}
const deleteDirectory = async (id: number) => {
  if (directoryNotAvailable('Управление папками')) {
    return
  }
  const directory = directories.value.find((dir) => {
    return dir.id === id
  })
  if (directory === undefined) {
    return
  }
  const modal = await openModal(RModalDeleteDirectory, {
    name: directory.name,
  })
  modal.on('delete', () => {
    directoriesStore.delete(id).then(() => {
      openDirectory(null)
    })
  })
}

const openDirectory = async (id: number | null) => {
  cardsStore.setFilter({ directory_id: id })
}
const search = async (text: string) => {
  cardsStore.setFilter({ search_string: text === '' ? null : text })
}

</script>

<template>
  <div class="container-lg">
    <div
      class="tab-content"
      id="nav-tabContent"
    >
      <NuxtLink
        v-if="type !== CardType.ProductCard"
        to="/"
        class="custom-link mt-4 d-block"
      >
        Перейти на главную страницу
      </NuxtLink>

      <div class="d-flex align-items-baseline">
        <h1
          class="head2 mb-0 me-3"
          :class="{ 'content-title': type === CardType.ProductCard }"
        >
          {{
            type === CardType.ProductCard ?
              'Карточки' : 'Шаблоны'
          }}
        </h1>
        <NuxtLink
          v-if="isAdmin"
          to="/users"
        >
          Пользователи
        </NuxtLink>
      </div>

      <ClientOnly>
        <RCards
          :type="type"
          :cards="list[type].cards"
          :total-count="list[type].totalCount ?? 0"
          :item-width="itemsSise.width"
          :item-height="itemsSise.height"
          :directories="directories"
          :selected-directory="filters.directory_id"
          :is-loading="isLoading[type]"
          :search-string="filters.search_string ?? ''"
          @load="load"
          @load-image="loadImage"
          @open="openCardOrPopup"
          @create-directory="createDirectory"
          @edit-directory="editDirectory"
          @delete-directory="deleteDirectory"
          @open-directory="openDirectory"
          @search="search"
          @clone="cloneCard"
          @edit="editCard"
          @delete="deleteCard"
          @put-to-directory="putCardToDirectory"
          @exclude-from-directory="excludeCardFromDirectory"
        />
      </ClientOnly>
      <div
        v-if="isLoading[type] && list[type].totalCount !== undefined"
        class="loading-more-area mt-4 mb-4"
      >
        <div
          class="spinner-border"
          role="status"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.tab-content {
  position: relative;

  .loading-area {
    position: absolute;
    width: 100%;
    text-align: center;

    .spinner-border {
      margin-top: calc(100% / 12);
    }
  }

  .loading-more-area {
    width: 100%;
    text-align: center;
  }

  .content-title {
    margin-top: rem(44px);

    @include media-breakpoint-down(lg) {
      margin-top: rem(40px);
    }

    @include media-breakpoint-down(md) {
      margin-top: rem(24px);
    }
  }
}
</style>
